import Icon from './Icon';
import { useFeeSchedule } from '../../../hooks/useFeeSchedule';

const FULL_LABELS = { CONTRACT: 'CONTRACT', STANDARD: 'STANDARD', MANUAL: 'MANUAL' };
const PAPER_LABELS = { CONTRACT: 'CTRCT', STANDARD: 'STD', MANUAL: 'MAN' };

const TONE = {
  CONTRACT: { bg: 'var(--ab-fg)',          fg: 'var(--ab-fg-inverse)', border: 'var(--ab-fg)' },
  STANDARD: { bg: 'var(--ab-bg)',          fg: 'var(--ab-fg-subtle)',  border: 'var(--ab-border-soft)' },
  MANUAL:   { bg: 'var(--ab-pending-soft)', fg: 'var(--ab-fg)',         border: 'var(--ab-pending-ring)' },
};

/**
 * @param {string} value
 * @param {string} cpt
 * @param {string|null} payerId
 * @param {{ resolveCharge: (cpt: string) => { amount: string|null, source: string } }} fee
 * @returns {'CONTRACT'|'STANDARD'|'MANUAL'|null}
 */
function deriveSource(value, cpt, payerId, fee) {
  if (!value) return null;
  const resolved = fee.resolveCharge(cpt);
  // No schedule entry to compare against — don't claim it's MANUAL,
  // we just don't know.
  if (resolved.amount == null) return null;
  if (String(value) === String(resolved.amount)) {
    const s = resolved.source;
    if (s === 'CONTRACT' || s === 'STANDARD') return s;
  }
  return 'MANUAL';
}

/**
 * @param {{
 *   value: string,
 *   cpt: string,
 *   payerId: string|null,
 *   onChange: (amount: string, nextSource: 'CONTRACT'|'STANDARD'|'MANUAL') => void,
 *   density?: 'default'|'paper',
 *   disabled?: boolean,
 * }} props
 */
export default function ChargeCell({ value, cpt, payerId, onChange, density = 'default', disabled = false }) {
  const fee = useFeeSchedule(payerId);
  const source = deriveSource(value, cpt, payerId, fee);
  const labels = density === 'paper' ? PAPER_LABELS : FULL_LABELS;
  const tone = source ? TONE[source] : null;

  const handleInput = (/** @type {any} */ e) => {
    const next = e.target.value;
    // Reject negative charge amounts at the input boundary — CMS-1500 Box 24F
    // is non-negative ($0 is allowed for non-billable lines, but not below 0).
    if (next !== '' && Number.isFinite(Number(next)) && Number(next) < 0) return;
    onChange?.(next, 'MANUAL');
  };
  const handleRevert = () => {
    const r = fee.resolveCharge(cpt);
    if (r.amount == null) return;
    onChange?.(r.amount, /** @type {'CONTRACT'|'STANDARD'} */ (r.source));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value || ''}
        disabled={disabled}
        onChange={handleInput}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: density === 'paper' ? 11 : 13,
          fontFamily: 'var(--ab-font-mono)',
          color: 'var(--ab-fg)',
          background: source === 'MANUAL' ? 'var(--ab-pending-soft)' : 'var(--ab-bg)',
          border: '1px solid var(--ab-fg)',
          borderRadius: 'var(--ab-radius-sm)',
          outline: 'none',
        }}
      />
      {source && tone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            title={
              source === 'CONTRACT' ? `Contracted rate.` :
              source === 'STANDARD' ? `Practice standard fee.` :
              `Manual override.`
            }
            style={{
              display: 'inline-block',
              padding: '0 4px',
              fontSize: 8,
              fontWeight: 'var(--ab-weight-bold)',
              letterSpacing: 'var(--ab-tracking-label)',
              color: tone.fg, background: tone.bg, border: `0.5px solid ${tone.border}`,
              borderRadius: 'var(--ab-radius-xs)',
            }}
          >
            {labels[source]}
          </span>
          {source === 'MANUAL' && (
            <button
              type="button"
              aria-label="Revert to schedule"
              onClick={handleRevert}
              disabled={disabled}
              style={{
                background: 'transparent', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
                color: 'var(--ab-fg-subtle)', display: 'inline-flex', padding: 0,
              }}
            >
              <Icon name="rotate-ccw" size={11} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
